import { action, makeObservable, observable } from 'mobx'
import { switchMap } from 'rxjs/operators'
import { CancellationToken } from 'infra/cancellation'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import Member from 'iam/models/Member'
import { CursorPageQuery, CursorPaged } from 'types/definitions'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'

export interface AdvisorSearchQuery extends CursorPageQuery {
  search_text?: string
}

export type SearchAdvisors = (
  query: AdvisorSearchQuery,
  cancellationToken?: CancellationToken,
) => Promise<CursorPaged<Member>>

/**
 * Paging and search for the advisor picker.
 */
export default class AdvisorSelectViewModel {
  readonly searchText = new SearchTextViewModel()

  /**
   * The advisor who will represent the invited client.
   */
  @observable selected: Member | null = null

  readonly scrollProvider: InfiniteScrollProvider<Member>

  /**
   * Refetches the first page whenever the search text changes.
   */
  readonly query$

  constructor(private readonly searchAdvisors: SearchAdvisors) {
    makeObservable(this)
    this.scrollProvider = new InfiniteScrollProvider<Member>({
      limit: 20,
      startFetchTaskAsPending: true,
      fetchItems: (params, cancellationToken) =>
        this.searchAdvisors(
          {
            limit: params.limit,
            after: params.after,
            search_text: this.searchText.value || undefined,
          },
          cancellationToken,
        ),
    })
    this.query$ = this.searchText.debouncedValue$.pipe(
      switchMap(() => this.scrollProvider.fetch()),
    )
  }

  /**
   * Updates the search text. The debounced stream reloads the list.
   */
  @action.bound
  triggerSearch(text: string) {
    this.searchText.change(text)
  }

  /**
   * Selects an advisor. Passing nothing clears the choice.
   */
  @action.bound
  select(advisor: Member | null | undefined) {
    this.selected = advisor ?? null
  }

  /**
   * Clears the choice and the search text.
   */
  @action.bound
  reset() {
    this.select(null)
    this.searchText.clear()
  }
}
